import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { fetchDashboardMeta, getCachedDashboardMeta, type DashboardMeta } from '../../lib/dashboard';
import { isWorkspaceSlug } from '../../lib/pages';
import { useBasePath } from '../../lib/use-base-path';
import { useDashboardDuplexer } from '../../lib/use-dashboard-duplexer';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useLoadBi } from '../../lib/use-load-bi';
import { useHomepage } from '../HomepageContext';
import { DashboardFrame, DashboardSkeleton } from './DashboardView';
import { useTranslation } from '../../lib/i18n';
import { useNarrow } from '../../lib/use-narrow';

const PAGE = 'dashboard';

// Renders ONE Orion surface — a dashboard bento (a bento of widgets) or a drill-in page (a slot-less detail
// layout) — through the SAME pipeline as the catch-up home: a render-handler existence probe resolves
// whether the dashboard/page exists (+ its name, via the document's own status/headers), and DashboardFrame
// points a sandboxed iframe's `src` at the DashboardRenderHandler for the finished document. So the workspace
// dashboard and every drill-in render identically to the home.
//
//  - `workspace` is the owning dashboard's slug: given → render that workspace's dashboard; omitted →
//    the home/catch-up dashboard. It also scopes a widget's onward drill-in (…/workspace/<ws>/pages/<page>).
//  - `page` selects a drill-in page within that dashboard; omitted → the dashboard's bento.
//  - `onBack` shows a subtle in-app back affordance (drill-in pages pass it); browser back works regardless.
//  - `missing` is shown when the target resolves empty (defaults to a quiet "not available").
export function DashboardScreen({
  page,
  workspace,
  onBack,
  missing,
  header,
}: {
  page?: string;
  workspace?: string;
  onBack?: () => void;
  missing?: ReactNode;
  // Optional chrome pinned to the top of the content area (the workspace tab bar). Rendered in every
  // branch so it's always present; passed with an explicit `active` by the caller (not derived from the
  // router) so hidden keep-alive instances of other workspaces still highlight the right tab. The
  // missing branch renders it via the caller-supplied `missing` node (e.g. ComingSoon's own `header`).
  header?: ReactNode;
}) {
  const target = { dashboard: workspace, page };
  // Seed from this target's module cache so re-entry paints instantly; undefined = probing, null =
  // resolved-but-empty (no such dashboard/page).
  const [meta, setMeta] = useState<DashboardMeta | null | undefined>(() => getCachedDashboardMeta(target));
  const [docHeight, setDocHeight] = useState<number | null>(null);
  // Bumped on a live edit so the iframe refetches (an unchanged document still 304s via its ETag).
  const [reloadKey, setReloadKey] = useState(0);

  // src:198 load — this surface's `fetchDashboardMeta({dashboard,page})` probe. `meta`
  // is undefined while probing (a cache hit seeds it defined on mount → cached render
  // emits nothing); a transient failure keeps the current render, so there's no
  // observable error signal here.
  useLoadBi('dashboard-web', { isLoading: meta === undefined });

  const load = useCallback(
    (force?: boolean) => {
      let cancelled = false;
      fetchDashboardMeta({ dashboard: workspace, page }, { force })
        .then((m) => {
          if (!cancelled) setMeta(m);
        })
        .catch(() => {
          // Transient failure — keep the current render; the next dashboard-changed signal or a refresh retries.
        });
      return () => {
        cancelled = true;
      };
    },
    [workspace, page],
  );

  // These routes share a pathname across slugs (/pages/[slug], /workspace/[slug]/pages/[page]), so this
  // component isn't remounted between them — re-seed from the new target's cache and re-probe on change.
  useEffect(() => {
    setMeta(getCachedDashboardMeta({ dashboard: workspace, page }));
    setDocHeight(null);
    return load();
  }, [workspace, page, load]);

  // Live update: when Orion rewrites this dashboard/page (a DashboardService write), the dashboard-changed
  // signal fires — re-probe and bump the iframe cache-buster so it re-renders.
  useDashboardDuplexer(() => {
    load(true);
    setReloadKey((k) => k + 1);
  });

  const router = useRouter();
  const basePath = useBasePath();
  const { state: homepage } = useHomepage();

  // Recovery: a GLOBAL drill-in page (no workspace) that resolves empty but names a known workspace is a
  // mis-addressed workspace nav that dead-ended as a `{ page }`. Send the user to the workspace's dashboard
  // rather than a dead-end "unavailable". Guarded to the ACTIVE `/pages/[slug]` route so a keep-alive
  // (hidden) instance of this screen never fires a redirect. Web-side net only — `{ workspace }` is an
  // orion-web-local destination (native + common-lib's `NavDestination` don't have it yet).
  const missingPageIsWorkspace =
    meta === null && !workspace && !!page && homepage.status === 'present' && isWorkspaceSlug(homepage.index, page);
  const isActivePageRoute = router.pathname === '/pages/[slug]' && router.query.slug === page;
  useEffect(() => {
    if (!missingPageIsWorkspace || !isActivePageRoute || !page) {
      return;
    }
    // eslint-disable-next-line no-console
    console.info('[dashboard-nav] page not found but matches a workspace — redirecting to its dashboard', { slug: page });
    void router.replace(
      { pathname: '/workspace/[slug]', query: { slug: page } },
      `${basePath}/workspace/${encodeURIComponent(page)}`,
    );
  }, [missingPageIsWorkspace, isActivePageRoute, page, basePath, router]);

  if (meta) {
    return (
      <Pane scroll={docHeight != null} onBack={onBack} header={header}>
        <DashboardFrame
          target={{ dashboard: workspace, page }}
          name={meta.name}
          workspace={workspace}
          reloadKey={reloadKey}
          docHeight={docHeight}
          onHeight={setDocHeight}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </Pane>
    );
  }
  if (meta === null) {
    // Redirecting a mis-addressed workspace nav (see above) — hold the loader instead of flashing "unavailable".
    if (missingPageIsWorkspace && isActivePageRoute) {
      return <LoadingPane header={header} />;
    }
    // A caller-supplied empty state (e.g. a workspace dashboard's "coming soon") owns its own pane and is a
    // top-level surface with no back affordance. It carries the top chrome itself (WorkspaceDashboard passes
    // the same tab bar into ComingSoon's `header`), so it renders as-is. The default "not available" is a
    // drill-in dead end, so it carries the same back button as the resolved page — otherwise the user is
    // stranded with no way back.
    if (missing) {
      return <>{missing}</>;
    }
    return (
      <Pane onBack={onBack} header={header}>
        <PageUnavailableMessage />
      </Pane>
    );
  }
  return <LoadingPane header={header} />;
}

// The content pane the page fills — the same absolute `.orion-content-pane` the home uses (which
// DashboardFrame's viewport logic keys on). In `scroll` mode the whole page scrolls around a content-sized
// iframe; otherwise the pane is fixed and the iframe fills it (pre-height fallback). An optional subtle
// back affordance sits above the content.
function Pane({
  children,
  scroll = false,
  onBack,
  header,
}: {
  children: ReactNode;
  scroll?: boolean;
  onBack?: () => void;
  header?: ReactNode;
}) {
  // Match AppShell's mobile top padding (64px band + 32px gap to the header, Figma 4176-6999 /
  // 4944-5613); desktop keeps 72px. Without this the workspace hero sits ~24px too high on mobile.
  const narrow = useNarrow();
  return (
    <section
      className="orion-content-pane"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        zIndex: 4,
        overflowY: scroll ? 'auto' : 'hidden',
        overflowX: 'hidden',
        paddingBlock: narrow ? '96px 40px' : '72px 40px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {header}
      {/* Desktop back affordance; on mobile the header back button covers it. */}
      {onBack && !narrow && <BackButton onClick={onBack} />}
      {scroll ? (
        <div style={{ position: 'relative', width: '100%', flexShrink: 0 }}>{children}</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>{children}</div>
      )}
    </section>
  );
}

// A quiet, low-contrast "return to the dashboard" affordance the drill-in pages carry.
function BackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <button
      type="button"
      onClick={() => {
        logPageClickBi({ page_name: PAGE, button_name: 'back' });
        onClick();
      }}
      style={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        margin: '0 0 14px',
        paddingBlock: 6,
        paddingInlineEnd: 12,
        paddingInlineStart: 8,
        border: 'none',
        borderRadius: 999,
        background: 'transparent',
        color: 'rgba(var(--ink-rgb),0.55)',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 'var(--sym-weight-medium)',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(var(--ink-rgb),0.05)';
        e.currentTarget.style.color = 'rgba(var(--ink-rgb),0.85)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = 'rgba(var(--ink-rgb),0.55)';
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden className="dir-flip">
        <path d="M9.5 3.5L5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t('common.back')}
    </button>
  );
}

// The default drill-in empty state — a centered "not available" note that fills the `Pane`'s (relative)
// content area, so the pane's back button sits above it exactly as it does over a resolved page.
function PageUnavailableMessage() {
  const { t } = useTranslation();
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ maxWidth: 420, textAlign: 'center', fontSize: 15, lineHeight: 1.6, color: 'rgba(var(--ink-rgb),0.5)' }}>
        {t('dashboard.pageUnavailable')}
      </p>
    </div>
  );
}

// Host-side placeholder while the assembled document is in flight — the same calm shimmer bento the home uses.
function LoadingPane({ header }: { header?: ReactNode }) {
  const narrow = useNarrow();
  return (
    <section
      className="orion-content-pane"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        zIndex: 4,
        overflow: 'hidden',
        paddingBlock: narrow ? '96px 40px' : '72px 40px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {header}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <DashboardSkeleton />
      </div>
    </section>
  );
}
